import React, { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Target, Info } from 'lucide-react';
import { WorkQueueTeamGoalsPanel, useTeamQueueGoalsSupervisor } from '@saas-foundation/shared-frontend/analytics';
import { TEAM_QUEUE_GOALS_INTRO } from '@saas-foundation/shared-frontend/workqueue/rcmWorkDomain';
import { TEAM_ANALYTICS_EMBED_REGISTRY } from '../../config/rcmServiceCodes';
import { formatShellResourceName } from '../../utils/shellResourceName';

/** Target column label per rcm_service_code (aligned with service queue semantics). */
const GOALS_UNIT_BY_SERVICE = {
  denial_management: 'claims',
  claims_processing: 'claims',
  charge_capture: 'charges',
  insurance_verification: 'verifications',
  prior_authorization: 'requests',
  ar_posting: 'items',
  remittance_advice: 'lines',
  contract_management: 'contracts',
  patient_billing: 'items',
  edi_platform: 'items',
};

const SERVICE_OPTIONS = TEAM_ANALYTICS_EMBED_REGISTRY.filter((e) => e.available !== false);

const PP_SG_RESOURCE_NAME = formatShellResourceName('provisioning-enablement', 'pp-sg');

/**
 * App-level supervisor UI: set SaaS team-queue-goals per RCM service (human + Kai).
 * @param {{ embedded?: boolean }} props — when true, rendered inside Work Queue (no standalone page chrome).
 */
const TeamQueueGoals = ({ embedded = false }) => {
  const queryClient = useQueryClient();
  const { organizationId, isSupervisor, availableUsers } = useTeamQueueGoalsSupervisor();
  const [rcmServiceCode, setRcmServiceCode] = useState(
    () => SERVICE_OPTIONS[0]?.rcmServiceCode || 'denial_management'
  );

  const unitLabel = useMemo(
    () => GOALS_UNIT_BY_SERVICE[rcmServiceCode] || 'items',
    [rcmServiceCode]
  );

  const selectedLabel = useMemo(
    () => SERVICE_OPTIONS.find((o) => o.rcmServiceCode === rcmServiceCode)?.label || rcmServiceCode,
    [rcmServiceCode]
  );

  useEffect(() => {
    if (embedded) return undefined;
    const prev = document.title;
    document.title = `${PP_SG_RESOURCE_NAME} · Team queue goals`;
    return () => {
      document.title = prev;
    };
  }, [embedded]);

  const shellClass = embedded
    ? 'space-y-6'
    : 'max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8';

  return (
    <div
      className={shellClass}
      data-resource-name={PP_SG_RESOURCE_NAME}
    >
      {!embedded ? (
      <div className="flex items-start gap-3 mb-6">
        <div className="p-2 rounded-lg bg-indigo-100 text-indigo-700">
          <Target className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team queue goals</h1>
          <p className="mt-1 text-sm text-gray-600 max-w-3xl">
            {TEAM_QUEUE_GOALS_INTRO}
          </p>
        </div>
      </div>
      ) : (
      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <p className="text-sm text-gray-600">{TEAM_QUEUE_GOALS_INTRO}</p>
      </div>
      )}

      <div className="mb-6 flex flex-col sm:flex-row sm:items-end gap-4">
        <div className="flex-1 min-w-[220px]">
          <label htmlFor="team-goals-service" className="block text-sm font-medium text-gray-700 mb-1">
            RCM service
          </label>
          <select
            id="team-goals-service"
            value={rcmServiceCode}
            onChange={(e) => setRcmServiceCode(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            {SERVICE_OPTIONS.map((opt) => (
              <option key={opt.rcmServiceCode} value={opt.rcmServiceCode}>
                {opt.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Goals are stored per organization and <code className="text-xs bg-gray-100 px-1 rounded">{rcmServiceCode}</code>.
          </p>
        </div>
      </div>

      {!organizationId ? (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span>Select an organization to load and edit team queue goals.</span>
        </div>
      ) : null}

      {organizationId && !isSupervisor ? (
        <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-800">
          <Info className="w-5 h-5 flex-shrink-0 mt-0.5 text-gray-500" />
          <span>
            Only organization owners, admins, and supervisors can edit team queue goals. You can still view
            analytics in each service under Queue and team.
          </span>
        </div>
      ) : null}

      {organizationId && isSupervisor ? (
        <WorkQueueTeamGoalsPanel
          key={rcmServiceCode}
          rcmServiceCode={rcmServiceCode}
          organizationId={organizationId}
          isSupervisor={isSupervisor}
          availableUsers={availableUsers}
          queryClient={queryClient}
          targetUnitLabel={unitLabel}
        />
      ) : null}

      {organizationId && isSupervisor ? (
        <p className="mt-6 text-xs text-gray-500">
          Editing goals for: <strong>{selectedLabel}</strong>. Queue items and work completion still live in that
          service; this screen only configures targets.
        </p>
      ) : null}
    </div>
  );
};

export default TeamQueueGoals;
